// Copyright (c) 2026, Cyber Cube and contributors
// For license information, please see license.txt
//
// The field portal's boot and its own screen — `/inspect`, `/inspect/<name>` and
// `/inspect/scan` (`PHASE-9.md` §8.1's portal column).
//
// ## This file runs on every website page, so it must do nothing on almost all of
// them
//
// `frappe/templates/base.html:101` loops `web_include_js` for **every** page the
// website renders — `/login`, the Contractor Gate Pass web form, any Web Page a
// site owner adds. So `mount()` looks for a `[data-qhse-portal]` element and
// returns when there is none, and the stylesheet nests every rule under
// `.qhse-inspect`. A portal bundle that restyled `/login` would be this app's most
// visible defect and the least likely to be noticed by anyone testing `/inspect`.
//
// ## What the portal has and has not
//
// `frappe-web.bundle.js` imports `libs.bundle.js`, so jQuery, moment and
// `window.Sortable` are present; it imports `ui/messages.js`, so `frappe.show_alert`
// is present. It does **not** import `frappe/request.js` (no `frappe.call`) or
// `ui/dialog.js` (no `frappe.ui.Dialog`). Both absences are handled once and
// centrally rather than at each call site — `qhse.facility.net.call()` for the
// first (shipped at M9.9a for exactly this milestone) and
// `qhse.facility.render.dialog()`'s fallback for the second — so no screen in this
// module has to know which shell it is running in.
//
// ## The screens
//
// `/inspect/<name>` is **the same `capture.js`** the desk renders at
// `/app/facility/round/<name>`, restacked by CSS. §8.4 and §8.5 are one screen
// (§8.4's own heading says "one responsive screen"), and a second phone-only
// implementation is CONVENTIONS #24's drift on the surface where a drift becomes a
// wrong verdict.

frappe.provide("qhse.facility");

const R = () => qhse.facility.render;
const NET = () => qhse.facility.net;

//: The attribute the template writes and this module dispatches on. A literal in
//: `qhse/www/inspect*.html`, never a value from the database.
const MOUNT_SELECTOR = "[data-qhse-portal]";

qhse.facility.portal = {
	/** Find the mount and render the screen it names. Safe to call anywhere. */
	mount(root) {
		const host = (root || document).querySelector(MOUNT_SELECTOR);
		if (!host) return null;

		const screen = host.getAttribute("data-qhse-portal");
		host.classList.add("qhse-inspect");
		try {
			if (screen === "rounds") return this.renderRounds(host);
			if (screen === "round") {
				// `?asset=` is how `/inspect/scan` hands a hit back: the scanner
				// navigates rather than keeping the capture screen alive behind it,
				// because a camera left running behind a page transition is a hot
				// phone and a green LED nobody asked for.
				const asset = this._param("asset");
				return qhse.facility.capture
					.render(host, host.getAttribute("data-round"), {
						exitLabel: __("Back to my rounds"),
						onExit: () => {
							window.location.href = "/inspect";
						},
					})
					.then(() => {
						// `jumpToAsset` is what says "not in this round" by name —
						// the honest answer for a sticker scanned on the wrong floor
						// (PHASE-9.md §8.5's contract table).
						if (asset) qhse.facility.capture.jumpToAsset(asset, asset);
					});
			}
			if (screen === "scan") return this.renderScan(host);
		} catch (error) {
			// A screen that throws must not leave a blank page: a blank page is
			// indistinguishable from a deploy that never happened (D11's rule, the
			// same guard `shell.js` puts around the desk screens).
			// eslint-disable-next-line no-console
			console.error("qhse.facility.portal: screen failed to render", error);
			R().clear(host).appendChild(
				R().empty(
					__("This screen could not be rendered."),
					error && error.message ? String(error.message) : ""
				)
			);
		}
		return null;
	},

	/** `/inspect` — my rounds, then everyone else's.
	 *
	 * Two lists because "assigned to me" and "open on this site" are different
	 * questions and a field inspector needs the first one first. Both come from one
	 * permission-aware endpoint; a round the caller may not read is in neither.
	 */
	renderRounds(host) {
		R().clear(host).appendChild(R().empty(__("Loading your rounds…"), ""));
		return NET()
			.call("qhse.api.facility_pages.my_rounds", {})
			.then((payload) => {
				const container = R().clear(host);
				container.appendChild(this._header(__("Inspection rounds"), null));

				const assigned = (payload && payload.assigned) || [];
				const other = (payload && payload.other) || [];
				const today = (payload && payload.today) || null;

				container.appendChild(
					R().section(
						__("Assigned to me"),
						assigned.length
							? this._roundList(assigned, today)
							: R().empty(
									__("No round is assigned to you."),
									__("A planner assigns a round when its scope is resolved.")
							  )
					)
				);

				if (other.length) {
					container.appendChild(
						R().section(__("Other open rounds"), this._roundList(other, today))
					);
				}

				const scan = R().el(
					"a",
					{ class: "qhse-fac-button is-ghost qhse-inspect-scan", href: "/inspect/scan" },
					__("⬤ Scan an asset")
				);
				container.appendChild(scan);
			})
			.catch((error) => {
				R().clear(host).appendChild(
					R().empty(
						__("Your rounds could not be loaded."),
						error && error.message ? String(error.message) : ""
					)
				);
			});
	},

	_roundList(rows, today) {
		const list = R().el("div", { class: "qhse-inspect-rounds" });
		rows.forEach((row) => {
			const card = R().el("a", {
				class: "qhse-inspect-round",
				href: `/inspect/${encodeURIComponent(row.name)}`,
			});
			const head = R().el("div", { class: "qhse-inspect-round-head" });
			R().append(
				head,
				R().el("strong", {}, row.name),
				R().el("span", { class: "qhse-fac-chip" }, row.status || "")
			);
			const done = Number(row.entries_completed || 0);
			const total = Number(row.total_entries || 0);
			R().append(
				card,
				head,
				R().el("div", { class: "qhse-inspect-round-title" }, row.title || ""),
				R().el(
					"div",
					{ class: "qhse-inspect-round-meta" },
					// `total` of 0 is the fresh-site case and the honest reading is
					// "no assets yet", never `0/0` and never `NaN%`.
					total
						? __("{0} of {1} assets done · due {2}", [
								String(done),
								String(total),
								row.due_date || "—",
						  ])
						: __("Scope not resolved yet · due {0}", [row.due_date || "—"])
				)
			);
			if (today && row.due_date && row.due_date < today) {
				card.classList.add("is-overdue");
				card.appendChild(R().el("span", { class: "qhse-fac-chip is-bad" }, __("overdue")));
			}
			list.appendChild(card);
		});
		return list;
	},

	/** `/inspect/scan` — the camera on its own page.
	 *
	 * The server's `camera_capability` verdict arrives as data attributes, so the
	 * explanation for an unavailable camera is the one the server can give (a
	 * plain-HTTP non-loopback origin will never have a camera for anyone, in any
	 * browser) rather than the one this tab can guess.
	 */
	renderScan(host) {
		const secure = host.getAttribute("data-camera-secure") === "1";
		const reason = host.getAttribute("data-camera-reason") || "";
		const round = host.getAttribute("data-round") || "";

		const container = R().clear(host);
		container.appendChild(
			this._header(
				__("Scan an asset"),
				round ? `/inspect/${encodeURIComponent(round)}` : "/inspect"
			)
		);

		if (!secure && reason) {
			container.appendChild(
				R().banner("warn", __("The camera is unavailable on this site"), reason)
			);
		}

		const open = R().el(
			"button",
			{ type: "button", class: "qhse-fac-button is-primary" },
			__("Open the camera")
		);
		open.addEventListener("click", () => this._openScanner(round, secure ? "" : reason));
		container.appendChild(open);

		// M9.16 — opened immediately **only when there is a camera to open**.
		//
		// Arriving at `/inspect/scan` is itself the request to scan, so on a
		// handset the overlay should come straight up; the button stays because a
		// scanner that was closed must be re-openable without a page reload. But
		// where the camera is refused — this site is plain HTTP, and `W1.9`/`LS6`
		// caught it — the overlay degrades to a text box, and putting that over a
		// deliberately-empty page made the whole screen read as "a frappe modal
		// with a stock blue button", which is not this module's idiom and is not
		// what §8.1 draws for `/inspect/scan`.
		//
		// CONVENTIONS #2's rule, one level up: an unavailable capability is
		// invisible rather than a shell of itself. With no camera the page keeps
		// the manual path it already renders and says why, in a sentence, in
		// place.
		if (secure) this._openScanner(round, "");
		else container.appendChild(this._manualEntry(round));
		return null;
	},

	/** Type a tag instead of scanning it — `/inspect/scan`'s fallback path, on
	 * the page rather than inside an overlay that has no camera to show.
	 *
	 * Deliberately the same `resolve_tag` round trip the scanner uses: one
	 * definition of "what does this code mean" (CONVENTIONS #24), and the same
	 * refusal by name when the asset is not in this round.
	 */
	_manualEntry(round) {
		const wrap = R().el("div", { class: "qhse-inspect-manual" });
		const status = R().el("p", { class: "qhse-fac-scan-status" }, "");
		const field = R().input(__("Or type the asset tag"), "text", "", () => {}, {
			placeholder: __("FE-105"),
		});
		const go = R().el(
			"button",
			{ type: "button", class: "qhse-fac-button is-primary" },
			__("Open")
		);
		const submit = () => {
			const typed = field.control.value.trim();
			if (!typed) {
				status.textContent = __("Type a tag, or scan one.");
				return;
			}
			qhse.facility.scan.resolveTyped(typed, {
				round,
				status,
				onResolved: round
					? (resolved) => {
							window.location.href = `/inspect/${encodeURIComponent(
								round
							)}?asset=${encodeURIComponent(resolved.asset)}`;
							return true;
					  }
					: null,
			});
		};
		go.addEventListener("click", submit);
		field.control.addEventListener("keydown", (event) => {
			if (event.key === "Enter") submit();
		});
		R().append(wrap, field, go, status);
		return wrap;
	},

	_openScanner(round, cameraReason) {
		if (!qhse.facility.scan) return;
		qhse.facility.scan.open({
			round,
			cameraReason,
			onResolved: round
				? (resolved) => {
						// Within a round, a hit goes back to the capture screen with
						// the entry pre-selected. `round_queue` is what decides
						// whether the asset is actually in scope, and it says so by
						// name there rather than here.
						window.location.href = `/inspect/${encodeURIComponent(
							round
						)}?asset=${encodeURIComponent(resolved.asset)}`;
						return true;
				  }
				: null,
		});
	},

	/** One query-string parameter, or null. Never trusted — handed to an endpoint
	 * that gates on the document, exactly like the path segment beside it. */
	_param(name) {
		if (typeof window === "undefined" || !window.location) return null;
		try {
			return new URLSearchParams(window.location.search || "").get(name);
		} catch (error) {
			return null;
		}
	},

	_header(title, backHref) {
		const head = R().el("header", { class: "qhse-inspect-header" });
		if (backHref) {
			const back = R().el("a", { class: "qhse-inspect-back", href: backHref });
			R().append(back, R().icon("back", 16), R().el("span", {}, __("Back")));
			head.appendChild(back);
		}
		head.appendChild(R().el("h1", { class: "qhse-inspect-title" }, title));
		return head;
	},
};

// The portal has no router and no `frappe.pages`; a website page is a document
// load. `DOMContentLoaded` has usually already fired by the time a
// `web_include_js` bundle at the end of `<body>` runs, so both arms are needed —
// the readyState check is not belt-and-braces, it is the common case.
if (typeof document !== "undefined") {
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => qhse.facility.portal.mount());
	} else {
		qhse.facility.portal.mount();
	}
}
